import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ErrorCard, SuccessCard } from '../components/NotificationCard';

export default function AccountDeletion() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    reason: '',
    additionalInfo: '',
    confirmDeletion: false,
    confirmDataLoss: false,
    confirmFinal: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.confirmDeletion || !formData.confirmDataLoss || !formData.confirmFinal) {
      setError('Please confirm all required acknowledgments.');
      return;
    }

    if (!formData.reason.trim()) {
      setError('Please provide a reason for account deletion.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create deletion request document
      const deletionRequest = {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'No email provided',
        userPhone: user?.phoneNumber || 'No phone provided',
        reason: formData.reason.trim(),
        additionalInfo: formData.additionalInfo.trim(),
        status: 'pending',
        requestedAt: serverTimestamp(),
        confirmedDeletion: formData.confirmDeletion,
        confirmedDataLoss: formData.confirmDataLoss,
        confirmedFinal: formData.confirmFinal,
        requestId: `DEL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      await setDoc(doc(db, 'accountDeletionRequests', deletionRequest.requestId), deletionRequest);

      setSuccess(true);
      setFormData({
        reason: '',
        additionalInfo: '',
        confirmDeletion: false,
        confirmDataLoss: false,
        confirmFinal: false
      });
    } catch (err) {
      console.error('Error submitting deletion request:', err);
      setError('Failed to submit deletion request. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <SuccessCard>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Deletion Request Submitted</h2>
              <p className="text-gray-600 mb-6">
                Your account deletion request has been submitted successfully. Our team will review your request and contact you within 30 days to confirm the deletion process.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• We'll review your request within 30 days</li>
                  <li>• You'll receive a confirmation email</li>
                  <li>• Your data will be permanently deleted after confirmation</li>
                  <li>• You'll receive a final confirmation once completed</li>
                </ul>
              </div>
              <Link 
                to="/"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </SuccessCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Request Account Deletion
            </h1>
            <p className="text-gray-600">
              We're sorry to see you go. Please read the information below carefully before proceeding.
            </p>
          </div>

          {/* Important Information */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-red-900 mb-4">⚠️ Important Information</h2>
            <div className="space-y-3 text-red-800">
              <p><strong>Account deletion is permanent and cannot be undone.</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>All your personal data will be permanently deleted</li>
                <li>Your order history and transaction records will be removed</li>
                <li>Your membership benefits and access will be revoked</li>
                <li>Any pending orders or refunds will be processed before deletion</li>
                <li>You will lose access to all cooperative services</li>
              </ul>
            </div>
          </div>

          {/* Process Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-blue-900 mb-4">📋 Deletion Process</h2>
            <div className="space-y-3 text-blue-800">
              <ol className="list-decimal pl-5 space-y-2">
                <li><strong>Submit Request:</strong> Fill out the form below</li>
                <li><strong>Review Period:</strong> We'll review your request within 30 days</li>
                <li><strong>Confirmation:</strong> You'll receive a confirmation email</li>
                <li><strong>Data Processing:</strong> We'll process any pending transactions</li>
                <li><strong>Deletion:</strong> Your account and data will be permanently deleted</li>
                <li><strong>Final Notice:</strong> You'll receive a final confirmation</li>
              </ol>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Reason for Deletion */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason for Account Deletion *
              </label>
              <select
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                <option value="">Select a reason</option>
                <option value="privacy_concerns">Privacy concerns</option>
                <option value="no_longer_using">No longer using the service</option>
                <option value="duplicate_account">Duplicate account</option>
                <option value="security_concerns">Security concerns</option>
                <option value="unsatisfactory_service">Unsatisfactory service</option>
                <option value="moving_away">Moving away from the area</option>
                <option value="other">Other reason</option>
              </select>
            </div>

            {/* Additional Information */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Information (Optional)
              </label>
              <textarea
                value={formData.additionalInfo}
                onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="4"
                placeholder="Please provide any additional details about your reason for deletion..."
              />
            </div>

            {/* Confirmations */}
            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="confirmDeletion"
                  checked={formData.confirmDeletion}
                  onChange={(e) => handleInputChange('confirmDeletion', e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="confirmDeletion" className="ml-3 text-sm text-gray-700">
                  I understand that account deletion is permanent and cannot be undone. *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="confirmDataLoss"
                  checked={formData.confirmDataLoss}
                  onChange={(e) => handleInputChange('confirmDataLoss', e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="confirmDataLoss" className="ml-3 text-sm text-gray-700">
                  I understand that all my personal data, order history, and account information will be permanently deleted. *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="confirmFinal"
                  checked={formData.confirmFinal}
                  onChange={(e) => handleInputChange('confirmFinal', e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="confirmFinal" className="ml-3 text-sm text-gray-700">
                  I confirm that I want to proceed with the account deletion request. *
                </label>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <ErrorCard>
                <div className="text-sm">{error}</div>
              </ErrorCard>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                {isSubmitting ? 'Submitting Request...' : 'Submit Deletion Request'}
              </button>
              <Link
                to="/"
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-center"
              >
                Cancel
              </Link>
            </div>
          </form>

          {/* Contact Information */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
            <p className="text-gray-600 mb-4">
              If you have questions about account deletion or would like to discuss alternatives, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> privacy@merkadoekspress.com</p>
              <p><strong>Phone:</strong> +63 950-468-6668</p>
              <p><strong>Address:</strong> PPAC Cooperative, Philippines</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
